import { queryOptions } from '@tanstack/react-query';
import { QueryKeys } from 'utils/queryKeys';
import { ApiUsersPermissionsRepository } from './ApiUsersPermissionsRepository';
import type { PermissionsResult } from './types';

const SERVER_STALE_TIME_MS = 5 * 60 * 1000;
const FALLBACK_REFETCH_INTERVAL_MS = 5 * 1000;

// All permission queries hit the same endpoint and share one cache entry; they
// differ only in how they select from the response. Build them from one config
// so the fetch/caching policy lives in a single place.
const permissionsQuery = <TSelected = PermissionsResult>(select?: (data: PermissionsResult) => TSelected) =>
  queryOptions({
    queryKey: QueryKeys.usersPermissions,
    queryFn: () => ApiUsersPermissionsRepository.getPermissions(),
    staleTime: (query) => (query.state.data?.source === 'fallback' ? 0 : SERVER_STALE_TIME_MS),
    refetchInterval: (query) => (query.state.data?.source === 'fallback' ? FALLBACK_REFETCH_INTERVAL_MS : false),
    refetchOnWindowFocus: (query) => (query.state.data?.source === 'fallback' ? 'always' : false),
    select,
  });

export const usersPermissionsQueries = {
  permissions: permissionsQuery(),
  customRolesAccess: permissionsQuery((data) => ({
    hasCustomRoleAccess: data.permissions.custom_role_access.allowed,
  })),
  automationsAccess: permissionsQuery((data) => ({
    hasAutomationsAccess: data.permissions.access_automations.allowed,
  })),
  createAutomationsAccess: permissionsQuery((data) => ({
    hasCreateAutomationsAccess: data.permissions.create_automations.allowed,
  })),
  canPurchaseCollaboratorSeats: permissionsQuery((data) => ({
    canPurchaseCollaboratorSeats: data.permissions.purchase_collaborator_seats.allowed,
  })),
};
